<script lang="ts" setup>
import {ref, computed, watch, toRef} from 'vue'
import {storeToRefs} from 'pinia'
import type {TaskInstance, TaskWithInstance} from '@backend/types'
import {useAppStore} from '@/stores/app'
import {
  useTaskEditForm,
  durationOptions,
  energyOptions,
  reminderPresets,
  folderOptions,
} from '@/composables/useTaskEditForm'
import TagsMultiSelect from './TagsMultiSelect.vue'
import {
  Dialog,
  DialogScrollContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {Label} from '@/components/ui/label'
import {Separator} from '@/components/ui/separator'
import {Badge} from '@/components/ui/badge'
import {Switch} from '@/components/ui/switch'
import {Checkbox} from '@/components/ui/checkbox'
import {CalendarDateTime, type DateValue} from '@internationalized/date'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {Calendar} from '@/components/ui/calendar'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from '@/components/ui/collapsible'
import {CalendarDays, ChevronDown, X} from 'lucide-vue-next'

const props = defineProps<{
  task: TaskWithInstance
  taskInstance: TaskInstance | null
  open: boolean
  noAutoFocus?: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const appStore = useAppStore()
const {projects, tags, contacts} = storeToRefs(appStore)

const taskRef = toRef(props, 'task')
const taskInstanceRef = toRef(props, 'taskInstance')

const {
  editTitle,
  editNotes,
  editDuration,
  editEnergy,
  editDelegatedTo,
  editProjectId,
  editFolder,
  editTags,
  editDueDate,
  editDone,
  editAutoComplete,
  canConfigureAutoComplete,
  editReminders,
  editRecurringSchedule,
  editRecurringCondition,
  editRecurringStartAt,
  editRecurringEndAt,
  isAddingNewContact,
  newContactName,
  isSaving,
  hasChanges,
  changedFields,
  initializeEditFields,
  addReminder,
  removeReminder,
  handleNewContact,
  saveChanges,
} = useTaskEditForm(taskRef, taskInstanceRef)

const discardDialogOpen = ref(false)
const dueDatePopoverOpen = ref(false)
const recurringStartPopoverOpen = ref(false)
const recurringEndPopoverOpen = ref(false)

const dueTime = computed({
  get() {
    if (!editDueDate.value || !('hour' in editDueDate.value)) return '09:00'
    return `${String(editDueDate.value.hour).padStart(2, '0')}:${String(editDueDate.value.minute).padStart(2, '0')}`
  },
  set(val: string) {
    if (!editDueDate.value) return
    const [h, m] = val.split(':').map(Number)
    editDueDate.value = new CalendarDateTime(
      editDueDate.value.year,
      editDueDate.value.month,
      editDueDate.value.day,
      h ?? 9,
      m ?? 0
    )
  },
})

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      initializeEditFields()
    }
  },
  {immediate: true}
)

function handleClose() {
  if (hasChanges.value) {
    discardDialogOpen.value = true
  } else {
    emit('update:open', false)
  }
}

function discardAndClose() {
  discardDialogOpen.value = false
  emit('update:open', false)
}

async function handleSave() {
  const result = await saveChanges()
  if (result.success) {
    emit('update:open', false)
  }
}

function handleReminderSelect(value: string | number | bigint | Record<string, any> | null) {
  if (typeof value === 'string' && value) {
    addReminder(value)
  }
}

function selectDueDate(value: DateValue | undefined) {
  if (value) {
    const [h, m] = dueTime.value.split(':').map(Number)
    editDueDate.value = new CalendarDateTime(value.year, value.month, value.day, h ?? 9, m ?? 0)
  } else {
    editDueDate.value = undefined
  }
  dueDatePopoverOpen.value = false
}

function selectRecurringStartAt(value: DateValue | undefined) {
  editRecurringStartAt.value = value
  recurringStartPopoverOpen.value = false
}

function selectRecurringEndAt(value: DateValue | undefined) {
  editRecurringEndAt.value = value
  recurringEndPopoverOpen.value = false
}

function formatDate(date: DateValue): string {
  return `${date.day.toString().padStart(2, '0')}.${date.month.toString().padStart(2, '0')}.${date.year}`
}

function handleDelegatedToChange(value: string | number | bigint | Record<string, any> | null) {
  if (typeof value !== 'string') return
  if (value === '__new__') {
    isAddingNewContact.value = true
    editDelegatedTo.value = ''
  } else {
    editDelegatedTo.value = value === '__none__' ? '' : value
  }
}

function cancelNewContact() {
  isAddingNewContact.value = false
  newContactName.value = ''
}

function getReminderLabel(value: string): string {
  const preset = reminderPresets.find((p) => p.value === value)
  return preset ? preset.label : value
}
</script>

<template>
  <Dialog :open="open" @update:open="handleClose">
    <DialogScrollContent class="max-w-lg" @open-auto-focus="noAutoFocus && $event.preventDefault()">
      <DialogHeader>
        <DialogTitle>{{ $t('actions.edit_task') }}</DialogTitle>
        <DialogDescription class="sr-only">{{ $t('actions.edit_task') }}</DialogDescription>
      </DialogHeader>

      <div class="grid gap-4 py-2">
        <!-- Core: Title -->
        <div class="grid gap-2">
          <Label for="edit-title">{{ $t('fields.title') }}</Label>
          <Input id="edit-title" v-model="editTitle" />
        </div>

        <!-- Core: Notes -->
        <div class="grid gap-2">
          <Label for="edit-notes">{{ $t('fields.notes') }}</Label>
          <Textarea id="edit-notes" v-model="editNotes" rows="3" />
        </div>

        <Separator />

        <!-- Folder + Project row -->
        <div class="grid grid-cols-2 gap-4">
          <div class="grid gap-2">
            <Label>{{ $t('fields.folder') }}</Label>
            <Select v-model="editFolder">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="folder in folderOptions" :key="folder" :value="folder">
                  {{ $t('scopes.' + folder) }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="grid gap-2">
            <Label>{{ $t('fields.project') }}</Label>
            <Select
              :model-value="editProjectId || '__none__'"
              @update:model-value="editProjectId = $event === '__none__' ? '' : ($event as string)">
              <SelectTrigger>
                <SelectValue :placeholder="$t('options.none')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{{ $t('options.none') }}</SelectItem>
                <SelectItem v-for="project in projects" :key="project.project_id" :value="String(project.project_id)">
                  {{ project.title }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <!-- Organization: Delegated To (visible if set or folder is waiting) -->
        <div v-if="editDelegatedTo || task.delegated_to || editFolder === 'waiting'" class="grid gap-2">
          <Label>{{ $t('fields.delegated_to') }}</Label>
          <template v-if="!isAddingNewContact">
            <Select :model-value="editDelegatedTo || '__none__'" @update:model-value="handleDelegatedToChange">
              <SelectTrigger>
                <SelectValue :placeholder="$t('options.none')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{{ $t('options.none') }}</SelectItem>
                <SelectItem v-for="contact in contacts" :key="contact" :value="contact">
                  {{ contact }}
                </SelectItem>
                <SelectItem v-if="editDelegatedTo && !contacts.includes(editDelegatedTo)" :value="editDelegatedTo">
                  {{ editDelegatedTo }}
                </SelectItem>
                <SelectItem value="__new__">{{ $t('options.new_contact') }}</SelectItem>
              </SelectContent>
            </Select>
          </template>
          <template v-else>
            <div class="flex gap-2">
              <Input
                v-model="newContactName"
                :placeholder="$t('fields.delegated_to')"
                class="flex-1"
                @keyup.enter="handleNewContact" />
              <Button variant="outline" size="sm" @click="handleNewContact">OK</Button>
              <Button variant="ghost" size="sm" @click="cancelNewContact">
                {{ $t('actions.cancel') }}
              </Button>
            </div>
          </template>
        </div>

        <!-- Scheduling: Due Date -->
        <div class="grid gap-2">
          <Label>{{ $t('fields.due_date') }}</Label>
          <div class="flex gap-2">
            <Popover v-model:open="dueDatePopoverOpen">
              <PopoverTrigger as-child>
                <Button
                  variant="outline"
                  class="flex-1 justify-start text-left font-normal"
                  :class="{'text-muted-foreground': !editDueDate}">
                  <CalendarDays class="mr-2 h-4 w-4" />
                  <span v-if="editDueDate">{{ formatDate(editDueDate) }}</span>
                  <span v-else>{{ $t('fields.due_date') }}</span>
                  <button
                    v-if="editDueDate"
                    type="button"
                    class="ml-auto rounded-sm opacity-50 hover:opacity-100"
                    @click.stop="editDueDate = undefined">
                    <X class="h-3 w-3" />
                  </button>
                </Button>
              </PopoverTrigger>
              <PopoverContent class="w-auto p-0" align="start">
                <Calendar
                  :model-value="editDueDate"
                  locale="ru"
                  :week-starts-on="1"
                  @update:model-value="selectDueDate" />
              </PopoverContent>
            </Popover>
            <Input v-if="editDueDate" v-model="dueTime" type="time" class="w-[7rem]" />
          </div>
        </div>

        <div v-if="canConfigureAutoComplete" class="flex items-center justify-between gap-4 rounded-lg border p-3">
          <Label for="edit-auto-complete">{{ $t('fields.auto_complete') }}</Label>
          <Switch id="edit-auto-complete" v-model="editAutoComplete" />
        </div>

        <!-- Scheduling: Reminders (only when due date is set) -->
        <div v-if="editDueDate" class="grid gap-2">
          <Label>{{ $t('fields.reminders') }}</Label>
          <div v-if="editReminders.length" class="flex flex-wrap gap-1">
            <Badge v-for="(reminder, idx) in editReminders" :key="idx" variant="secondary" class="gap-1">
              {{ $t(getReminderLabel(reminder)) }}
              <button class="ml-0.5 rounded-sm hover:bg-muted" @click="removeReminder(idx)">
                <X class="h-3 w-3" />
              </button>
            </Badge>
          </div>
          <Select :model-value="'__placeholder__'" @update:model-value="handleReminderSelect">
            <SelectTrigger>
              <SelectValue :placeholder="$t('actions.add_reminder')" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="preset in reminderPresets"
                :key="preset.value"
                :value="preset.value"
                :disabled="editReminders.includes(preset.value)">
                {{ $t(preset.label) }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <!-- Scheduling: Recurring -->
        <Collapsible>
          <CollapsibleTrigger as-child>
            <Button variant="ghost" class="w-full justify-between px-0 font-normal">
              {{ $t('fields.recurring') }}
              <ChevronDown class="h-4 w-4" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div class="grid gap-3 pt-2">
              <div class="grid gap-2">
                <Label for="edit-schedule">{{ $t('fields.schedule') }}</Label>
                <Input id="edit-schedule" v-model="editRecurringSchedule" placeholder="e.g. 'every Monday at 10:00'" />
              </div>
              <div class="grid gap-2">
                <Label for="edit-condition">{{ $t('fields.condition') }}</Label>
                <Input id="edit-condition" v-model="editRecurringCondition" placeholder="e.g. '+10 days'" />
              </div>
              <div class="grid gap-2">
                <Label>{{ $t('fields.start_date') }}</Label>
                <Popover v-model:open="recurringStartPopoverOpen">
                  <PopoverTrigger as-child>
                    <Button
                      variant="outline"
                      class="w-full justify-start text-left font-normal"
                      :class="{'text-muted-foreground': !editRecurringStartAt}">
                      <CalendarDays class="mr-2 h-4 w-4" />
                      <span v-if="editRecurringStartAt">{{ formatDate(editRecurringStartAt) }}</span>
                      <span v-else>{{ $t('fields.start_date') }}</span>
                      <button
                        v-if="editRecurringStartAt"
                        type="button"
                        class="ml-auto rounded-sm opacity-50 hover:opacity-100"
                        @click.stop="editRecurringStartAt = undefined">
                        <X class="h-3 w-3" />
                      </button>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent class="w-auto p-0" align="start">
                    <Calendar
                      :model-value="editRecurringStartAt"
                      locale="ru"
                      :week-starts-on="1"
                      @update:model-value="selectRecurringStartAt" />
                  </PopoverContent>
                </Popover>
              </div>
              <div class="grid gap-2">
                <Label>{{ $t('fields.end_date') }}</Label>
                <Popover v-model:open="recurringEndPopoverOpen">
                  <PopoverTrigger as-child>
                    <Button
                      variant="outline"
                      class="w-full justify-start text-left font-normal"
                      :class="{'text-muted-foreground': !editRecurringEndAt}">
                      <CalendarDays class="mr-2 h-4 w-4" />
                      <span v-if="editRecurringEndAt">{{ formatDate(editRecurringEndAt) }}</span>
                      <span v-else>{{ $t('fields.end_date') }}</span>
                      <button
                        v-if="editRecurringEndAt"
                        type="button"
                        class="ml-auto rounded-sm opacity-50 hover:opacity-100"
                        @click.stop="editRecurringEndAt = undefined">
                        <X class="h-3 w-3" />
                      </button>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent class="w-auto p-0" align="start">
                    <Calendar
                      :model-value="editRecurringEndAt"
                      locale="ru"
                      :week-starts-on="1"
                      @update:model-value="selectRecurringEndAt" />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Separator />

        <div class="flex items-center justify-between gap-4 rounded-lg border p-3">
          <Label for="edit-done">{{ $t('fields.done') }}</Label>
          <Checkbox id="edit-done" v-model="editDone" />
        </div>

        <!-- Duration + Energy row -->
        <div class="grid grid-cols-2 gap-4">
          <div class="grid gap-2">
            <Label>{{ $t('fields.duration') }}</Label>
            <Select
              :model-value="editDuration || '__none__'"
              @update:model-value="editDuration = $event === '__none__' ? '' : ($event as string)">
              <SelectTrigger>
                <SelectValue :placeholder="$t('options.none')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="opt in durationOptions"
                  :key="opt.value || '__none__'"
                  :value="opt.value || '__none__'">
                  {{ $t(opt.label) }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="grid gap-2">
            <Label>{{ $t('fields.energy') }}</Label>
            <Select
              :model-value="editEnergy || '__none__'"
              @update:model-value="editEnergy = $event === '__none__' ? '' : ($event as string)">
              <SelectTrigger>
                <SelectValue :placeholder="$t('options.none')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="opt in energyOptions"
                  :key="opt.value || '__none__'"
                  :value="opt.value || '__none__'">
                  {{ $t(opt.label) }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <!-- Organization: Tags -->
        <div class="grid gap-2">
          <Label>{{ $t('fields.tags') }}</Label>
          <TagsMultiSelect v-model="editTags" :tags="tags" />
        </div>
      </div>

      <DialogFooter class="flex-row gap-2">
        <Button variant="outline" class="flex-1 sm:flex-none" @click="handleClose">{{ $t('actions.cancel') }}</Button>
        <Button class="flex-1 sm:flex-none" :disabled="!hasChanges || isSaving" @click="handleSave">
          {{ $t('actions.save') }}
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>

  <!-- Unsaved changes confirmation -->
  <AlertDialog :open="discardDialogOpen" @update:open="discardDialogOpen = $event">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ $t('messages.unsaved_changes') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ $t('messages.unsaved_changes_confirm') }}
          <span
            v-for="c in changedFields.slice(0, 4)"
            :key="c.field"
            class="block mt-1 text-xs text-muted-foreground font-mono">
            {{ $t(`fields.${c.field}`) }}: {{ c.from }} → {{ c.to }}
          </span>
          <span v-if="changedFields.length > 4" class="block mt-1 text-xs text-muted-foreground">
            +{{ changedFields.length - 4 }} more
          </span>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>{{ $t('actions.continue_editing') }}</AlertDialogCancel>
        <AlertDialogAction @click="discardAndClose">{{ $t('actions.discard') }}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
